import axios from 'axios';
import CartInterface from '@/interfaces/CartInterface';
import AjaxResponseInterface from '@/interfaces/AjaxResponseInterface';
import ValidationResponseInterface from '@/interfaces/ValidationResponseInterface';
import { getCookie } from '@/helpers';
import { mapCartResponse } from '@/classes/Product';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import SupplierInterface from '@/interfaces/SupplierInterface';

class OrderAPI {
  static validate(step: string, data: unknown): Promise<ValidationResponseInterface> {
    return new Promise<ValidationResponseInterface>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<ValidationResponseInterface>(`${ajaxUrl}?action=order`, {
        endpoint: 'validate',
        step,
        data,
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as ValidationResponseInterface;

        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static submit(data: unknown): Promise<ValidationResponseInterface> {
    return new Promise<ValidationResponseInterface>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<ValidationResponseInterface>(`${ajaxUrl}?action=order`, {
        endpoint: 'submit',
        cartKey: getCookie('io_shop_cart'),
        data,
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as ValidationResponseInterface;

        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getFormData(): Promise<Record<string, unknown>> {
    return new Promise<Record<string, unknown>>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.get<Record<string, unknown>>(ajaxUrl, {
        params: {
          action: 'order',
          endpoint: 'getFormData',
        },
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getLastOrderedItem(): Promise<Record<string, unknown>> {
    return new Promise<Record<string, unknown>>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.get<Record<string, unknown>>(ajaxUrl, {
        params: {
          action: 'order',
          endpoint: 'getLastOrderedItem',
        },
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static deleteLastOrderedItem(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'deleteLastOrderedItem',
      }).then(() => {
        resolve(true);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static activate(hash: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'activate',
        hash,
      }).then((response) => {
        if (response.status !== 200) {
          resolve(false);
        }
        resolve(true);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static storeIncompleteOrder(orderData: Record<any, unknown>, email: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'storeIncomplete',
        orderData,
        email,
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        if (
          typeof ajaxResponseData.tokens === 'string'
          && ajaxResponseData.tokens.length > 0
        ) {
          resolve(ajaxResponseData.tokens);
        } else {
          reject(new Error('unable to store order'));
        }
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static validateIncompleteOrderToken(token: string): Promise<{
    cartData: CartInterface,
    cartEnabled: boolean,
    cartKey: string,
  }> {
    return new Promise((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post(`${ajaxUrl}?action=order`, {
        endpoint: 'validateIncompleteOrderToken',
        token,
      }).then((response) => {
        resolve({
          cartData: mapCartResponse(response),
          cartEnabled: (response?.data?.data?.cartEnabled || false) as boolean,
          cartKey: (response?.data?.data?.cartKey || '') as string,
        });
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getCitiesAndStreets(postcode: string | number)
      : Promise<{
    postcode: string | number,
    cities: CityInterface[],
    streets: StreetInterface[],
  }> {
    return new Promise((resolve, reject) => {
      axios.get<CityInterface[]>(`${window.ioappConfig.ajaxUrl}?action=order`, {
        params: {
          endpoint: 'citiesAndStreets',
          postcode,
        },
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        resolve({
          postcode,
          streets: ajaxResponseData.streets as StreetInterface[],
          cities: ajaxResponseData.cities as CityInterface[],
        });
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getSuppliers(branch = 'all')
      : Promise<{
    branch: string,
    supplier: SupplierInterface[],
  }> {
    return new Promise((resolve, reject) => {
      axios.get<SupplierInterface[]>(`${window.ioappConfig.ajaxUrl}?action=order`, {
        params: {
          endpoint: 'getSuppliers',
          branch,
        },
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        resolve({
          branch,
          supplier: ajaxResponseData.supplier as SupplierInterface[],
        });
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getAllSuppliers()
      : Promise<{
    power: SupplierInterface[],
    gas: SupplierInterface[],
  }> {
    return new Promise((resolve, reject) => {
      axios.get<{
        power: SupplierInterface[],
        gas: SupplierInterface[],
      }>(`${window.ioappConfig.ajaxUrl}?action=order`, {
        params: {
          endpoint: 'getSuppliers',
          branch: 'all',
        },
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const supplier = ajaxResponse.data as {
          power: SupplierInterface[],
          gas: SupplierInterface[],
        };
        resolve(supplier);
      }).catch((e) => {
        reject(e);
      });
    });
  }
}

export default OrderAPI;
